import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { disputesApi } from "../services/disputes";
import type { DisputeView } from "../types/disputes";
import "./DisputeUploadPage.css";
import { copyTextToClipboard } from "../lib/clipboard";
import { formatBytes } from "../utils/formatBytes";
import { classifyUploadExtension } from "../utils/uploadFileKind";
import { publicUrl } from "../utils/publicUrl";
import { t as tr } from "../i18n";
import { T } from "../i18n/T";

// Talent-facing dispute upload page (Phase 3).
//
// Reached at /disputes/:id/upload. Auth-required. The talent uploads their
// DJ-software track-history export (or screenshot fallback) to bolster the
// dispute evidence packet that auto-submits to Stripe Smart Disputes.
//
// File goes through POST /api/disputes/:id/upload → multer in-memory →
// Stripe Files API. Nothing persists on our infra; the authoritative copy
// lives at Stripe attached to the dispute.

const ACCEPTED_EXTENSIONS = [".csv", ".txt", ".xml", ".m3u", ".m3u8", ".png", ".jpg", ".jpeg"];
const ACCEPT_ATTR = ACCEPTED_EXTENSIONS.join(",");
const MAX_BYTES = 25 * 1024 * 1024;

function fmtMoney(cents: number | null | undefined, currency: string): string {
  if (typeof cents !== "number") return "—";
  const code = (currency || "usd").toUpperCase();
  const symbol = code === "EUR" ? "€" : code === "GBP" ? "£" : "$";
  return `${symbol}${(cents / 100).toFixed(2)}`;
}

function fmtDateLong(d: string | null): string {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleString([], {
      month: "long",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
}

function useCountdown(target: string | null): string {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!target) return;
    const id = setInterval(() => setTick((t) => t + 1), 60_000);
    return () => clearInterval(id);
  }, [target]);

  if (!target) return "";
  const ms = new Date(target).getTime() - Date.now();
  if (ms <= 0) return tr("disputeUploadPage.pastDeadline");
  // Mark tick as read so the linter doesn't strip it; the value itself is unused.
  void tick;
  const totalMinutes = Math.floor(ms / 60_000);
  const days = Math.floor(totalMinutes / (60 * 24));
  const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
  if (days > 0) return `${days} day${days === 1 ? "" : "s"}, ${hours} hour${hours === 1 ? "" : "s"}`;
  const minutes = totalMinutes % 60;
  return `${hours} hour${hours === 1 ? "" : "s"}, ${minutes} minute${minutes === 1 ? "" : "s"}`;
}

const DisputeUploadPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();

  const [dispute, setDispute] = useState<DisputeView | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [linkCopied, setLinkCopied] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const refresh = useCallback(async () => {
    if (!id) return;
    setLoadError(null);
    try {
      const data = await disputesApi.get(id);
      setDispute(data);
    } catch (e) {
      const status = (e as { response?: { status?: number } })?.response?.status;
      if (status === 403) setLoadError(tr("disputeUploadPage.youDonTHave"));
      else if (status === 404) setLoadError(tr("disputeUploadPage.thisDisputeDoesnT"));
      else setLoadError(tr("disputeUploadPage.couldnTLoadThis2"));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const countdown = useCountdown(dispute?.evidenceDueBy || null);
  const readOnly = !!(dispute && (dispute.pastDeadline || dispute.closed));
  const acceptedFileType = useMemo(
    () => (file ? classifyUploadExtension(file.name) : null),
    [file]
  );

  function handleFiles(list: FileList | File[] | null) {
    setFileError(null);
    setSubmitError(null);
    if (!list || list.length === 0) return;
    const first = list[0];
    const klass = classifyUploadExtension(first.name);
    if (!klass) {
      setFileError(tr("disputeUploadPage.thatFileTypeIsn", { ACCEPTED_EXTENSIONS: ACCEPTED_EXTENSIONS.join(", ") }));
      return;
    }
    if (first.size > MAX_BYTES) {
      setFileError(tr("disputeUploadPage.fileIsTooLarge", { formatBytes: formatBytes(first.size) }));
      return;
    }
    setFile(first);
  }

  function onCopyLink() {
    if (typeof window === "undefined") return;
    const url = publicUrl();
    copyTextToClipboard(url).then(() => {
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    });
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!file || !id || submitting) return;

    setSubmitting(true);
    setSubmitError(null);
    const fd = new FormData();
    fd.append("file", file);
    if (notes.trim()) fd.append("notes", notes.trim());

    try {
      await disputesApi.upload(id, fd);
      setFile(null);
      setNotes("");
      if (fileInputRef.current) fileInputRef.current.value = "";
      await refresh();
    } catch (e) {
      const err = e as { response?: { status?: number; data?: { msg?: string } } };
      const status = err?.response?.status;
      const msg = err?.response?.data?.msg;
      if (status === 413) setSubmitError(msg || tr("disputeUploadPage.fileTooLarge"));
      else if (status === 415) setSubmitError(msg || tr("disputeUploadPage.fileTypeNotAccepted"));
      else if (status === 410) setSubmitError(msg || tr("disputeUploadPage.theDeadlineToAdd"));
      else if (status === 502) setSubmitError(msg || tr("disputeUploadPage.weCouldnTStore"));
      else setSubmitError(tr("disputeUploadPage.uploadFailedPleaseTry"));
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div className="dispute-upload-page">
        <div className="dispute-upload-card">
          <p className="dispute-upload-loading">{tr("disputeUploadPage.loading")}</p>
        </div>
      </div>
    );
  }

  if (loadError || !dispute) {
    return (
      <div className="dispute-upload-page">
        <div className="dispute-upload-card">
          <h1 className="dispute-upload-title">{tr("disputeUploadPage.couldnTLoadThis")}</h1>
          <p className="dispute-upload-subtitle">{loadError || tr("disputeUploadPage.unknownError")}</p>
        </div>
      </div>
    );
  }

  const order = dispute.order;
  const moneyStr = fmtMoney(dispute.amount, dispute.currency);

  return (
    <div className="dispute-upload-page">
      <div className="dispute-upload-card">
        <div className="dispute-upload-header">
          <h1 className="dispute-upload-title">
            <T k="disputeUploadPage.defendYourEarnings" tags={{ 0: (c) => <span className="dispute-upload-title-emoji" aria-hidden>{c}</span> }} />
          </h1>
          <p className="dispute-upload-subtitle">
            {tr("disputeUploadPage.aCustomerDisputedA", { moneyStr })}
          </p>
        </div>

        {order && (
          <div className="dispute-upload-order">
            <div className="dispute-upload-order-row">
              <span className="dispute-upload-order-label">{tr("disputeUploadPage.song")}</span>
              <span className="dispute-upload-order-value">
                {order.searchQuery || "—"}
                {order.trackArtists.length ? (
                  <span className="dispute-upload-order-sub"> — {order.trackArtists.join(", ")}</span>
                ) : null}
              </span>
            </div>
            <div className="dispute-upload-order-row">
              <span className="dispute-upload-order-label">{tr("disputeUploadPage.amount")}</span>
              <span className="dispute-upload-order-value">{moneyStr}</span>
            </div>
            <div className="dispute-upload-order-row">
              <span className="dispute-upload-order-label">{tr("disputeUploadPage.played")}</span>
              <span className="dispute-upload-order-value">
                {fmtDateLong(order.playedAt)}
                {order.venueAtPlayed ? <span className="dispute-upload-order-sub">{tr("disputeUploadPage.at", { venueAtPlayed: order.venueAtPlayed })}</span> : null}
              </span>
            </div>
            <div className="dispute-upload-order-row">
              <span className="dispute-upload-order-label">{tr("disputeUploadPage.customer")}</span>
              <span className="dispute-upload-order-value">{order.customerName || "—"}</span>
            </div>
          </div>
        )}

        {!readOnly && (
          <div className="dispute-upload-reassure">
            <div className="dispute-upload-reassure-title">
              {tr("disputeUploadPage.weVeAlreadyAuto")}
            </div>
            <p className="dispute-upload-reassure-body">
              <T k="disputeUploadPage.theBankHasThe" tags={{ 0: (c) => <strong>{c}</strong> }} />
            </p>
          </div>
        )}

        {!readOnly && dispute.evidenceDueBy && (
          <div className="dispute-upload-deadline">
            <div className="dispute-upload-deadline-label">{tr("disputeUploadPage.timeRemaining")}</div>
            <div className="dispute-upload-deadline-value">{countdown}</div>
            <div className="dispute-upload-deadline-sub">
              {tr("disputeUploadPage.stripeGivesUsUntil", { amount: fmtDateLong(dispute.evidenceDueBy) })}
            </div>
          </div>
        )}

        {readOnly ? (
          <div className="dispute-upload-readonly">
            <div className="dispute-upload-readonly-title">
              {dispute.upload.uploaded ? tr("disputeUploadPage.evidenceAlreadySubmitted") : tr("disputeUploadPage.theDeadlineHasPassed")}
            </div>
            <p className="dispute-upload-readonly-body">
              {dispute.upload.uploaded ? (
                <>
                  {tr("disputeUploadPage.thanksForYourHelp")}
                </>
              ) : (
                <>
                  {tr("disputeUploadPage.thisDisputeIsClosed")}
                </>
              )}
            </p>
            {dispute.upload.uploaded && (
              <div className="dispute-upload-uploaded-card">
                <div className="dispute-upload-uploaded-row">
                  <span className="dispute-upload-uploaded-label">{tr("disputeUploadPage.file")}</span>
                  <span className="dispute-upload-uploaded-value">{dispute.upload.filename}</span>
                </div>
                <div className="dispute-upload-uploaded-row">
                  <span className="dispute-upload-uploaded-label">{tr("disputeUploadPage.type")}</span>
                  <span className="dispute-upload-uploaded-value">
                    {dispute.upload.fileType === "export"
                      ? tr("disputeUploadPage.strongEvidenceTrackHistory")
                      : tr("disputeUploadPage.acceptableScreenshot")}
                  </span>
                </div>
                <div className="dispute-upload-uploaded-row">
                  <span className="dispute-upload-uploaded-label">{tr("disputeUploadPage.uploaded")}</span>
                  <span className="dispute-upload-uploaded-value">{fmtDateLong(dispute.upload.uploadedAt)}</span>
                </div>
              </div>
            )}
          </div>
        ) : (
          <form className="dispute-upload-form" onSubmit={onSubmit}>
            {dispute.upload.uploaded && (
              <div className="dispute-upload-current">
                <div className="dispute-upload-current-pill">
                  {dispute.upload.fileType === "export" ? tr("disputeUploadPage.strongEvidenceAttached") : tr("disputeUploadPage.screenshotAttached")}
                </div>
                <div className="dispute-upload-current-body">
                  <T k="disputeUploadPage.fileUploadedReplace" vars={{ file: dispute.upload.filename ?? "", date: fmtDateLong(dispute.upload.uploadedAt) }} tags={{ 0: (c) => <strong>{c}</strong> }} />
                </div>
              </div>
            )}

            {!file && (
              <div className="dispute-upload-arrow-wrap" aria-hidden>
                <svg
                  className="dispute-upload-arrow-icon"
                  viewBox="0 0 48 48"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="24" y1="6" x2="24" y2="40" />
                  <polyline points="10,26 24,40 38,26" />
                </svg>
                <div className="dispute-upload-arrow-label">
                  {tr("disputeUploadPage.uploadYourTrackHistory")}
                </div>
              </div>
            )}

            <div
              className={`dispute-upload-drop ${dragOver ? "is-drag" : ""} ${!file ? "is-empty" : ""}`}
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragOver(false);
                handleFiles(e.dataTransfer?.files || null);
              }}
              onClick={() => fileInputRef.current?.click()}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") fileInputRef.current?.click();
              }}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPT_ATTR}
                style={{ display: "none" }}
                onChange={(e) => handleFiles(e.target.files)}
              />
              {file ? (
                <div className="dispute-upload-selected">
                  <div className="dispute-upload-selected-name">{file.name}</div>
                  <div className="dispute-upload-selected-meta">
                    {formatBytes(file.size)}
                    {acceptedFileType ? (
                      <>
                        {" · "}
                        <span className={`dispute-upload-type dispute-upload-type-${acceptedFileType}`}>
                          {acceptedFileType === "export"
                            ? tr("disputeUploadPage.strongEvidenceTrackHistory")
                            : tr("disputeUploadPage.acceptableScreenshot")}
                        </span>
                      </>
                    ) : null}
                  </div>
                </div>
              ) : (
                <>
                  <div className="dispute-upload-drop-title">{tr("disputeUploadPage.dropYourTrackHistory")}</div>
                  <div className="dispute-upload-drop-sub">{tr("disputeUploadPage.orClickToBrowse")}</div>
                </>
              )}
            </div>

            {fileError && <div className="dispute-upload-error">{fileError}</div>}

            <div className="dispute-upload-copy-link" aria-live="polite">
              <span className="dispute-upload-copy-link-text">
                {tr("disputeUploadPage.onMobileCopyThis")}
              </span>
              <button
                type="button"
                className="dispute-upload-copy-link-btn"
                onClick={onCopyLink}
                aria-label={tr("disputeUploadPage.copyLinkToClipboard")}
                title={tr("disputeUploadPage.copyLink")}
              >
                {linkCopied ? (
                  <span className="dispute-upload-copy-link-btn-done">{tr("disputeUploadPage.copied")}</span>
                ) : (
                  <>
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden
                    >
                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                    </svg>
                    <span>{tr("disputeUploadPage.copy")}</span>
                  </>
                )}
              </button>
            </div>

            <p className="dispute-upload-helper">
              <T k="disputeUploadPage.forMaximumChanceOf" vars={{ amount: fmtDateLong(order?.playedAt || null) }} tags={{ 0: (c) => <strong>{c}</strong>, 1: (c) => <strong>{c}</strong>, 2: (c) => <strong>{c}</strong>, 3: (c) => <strong>{c}</strong>, 4: (c) => <strong>{c}</strong> }} />
            </p>

            <label className="dispute-upload-notes-label">
              <T k="disputeUploadPage.addAnyContextFor" vars={{ length: notes.length }} tags={{ 0: <textarea
                className="dispute-upload-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value.slice(0, 250))}
                placeholder={tr("disputeUploadPage.eGTheCustomer")}
                maxLength={250}
                rows={4}
              />, 1: (c) => <span className="dispute-upload-notes-count">{c}</span> }} />
            </label>

            {submitError && <div className="dispute-upload-error">{submitError}</div>}

            <button
              type="submit"
              className="dispute-upload-submit"
              disabled={!file || submitting}
            >
              {submitting ? tr("disputeUploadPage.uploading") : tr("disputeUploadPage.attachToMyDispute")}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default DisputeUploadPage;
